<template>
  <div
    v-if="hasAccess(MARKETING_MATERIALS_ACCESS, MARKETING_MATERIALS_MANAGE_ACCESS)"
    class="marketing-materials banners"
    ref="Banners"
  >
    <h1>Banners</h1>

    <template v-if="pages && pages.length === 0">
      <notification-financing-page />
    </template>

    <template v-else-if="pages">
      <PageSelector />

      <div class="columns">
        <div class="column banners-title">
          <p>
            {{ text }}
          </p>
        </div>
      </div>

      <b-field
        grouped
        group-multiline
        class="field-filters"
      >
        <b-field
          v-if="themes.length > 1"
          :expanded="windowWidth<500"
          label="Theme"
          custom-class="custom-label"
          class="banner-theme-selector"
        >
          <b-select
            v-model="filters.themeId"
            :expanded="windowWidth<500"
            class="banner-selector" id="bannerSelector"
          >
            <option
              v-for="(option, key) in themes"
              :value="option.id"
              :key="key"
            >
              {{ option.name }}
            </option>
          </b-select>
        </b-field>

        <b-field style="margin-bottom: 0.75rem;">
          <ModernRadioTabs
            v-model="filters.bannerSizeKey"
            :items="bannerSizes"
            value-key="key"
            display-value-key="key"
            label="Size"
          />
        </b-field>
      </b-field>

      <div class="chooseSwitch">
        <div class="chooseSwitchText">
          Go to a co-branded page by clicking on banner

          <b-tooltip
            :delay="200"
            :position="getTooltipPositionByWindowWidth(600, 'is-top')"
            label="Turn this toggle on to send users to your co-branded page
                   when they click on the banner.
                   The toggle is switched on by default."
            class="is-gray"
            type="is-info"
            size="is-small"
            animated
            multilined
          >
            <b-icon pack="fa" size="is-small" icon="question-circle" />
          </b-tooltip>
        </div>

        <b-switch
          v-model="filters.hideLink"
          class="enh-switch"
          position="is-right"
          size="is-small"
          type="is-green"
        />
      </div>
    </template>

    <div ref="bannersWrapper" class="banners-wrapper">
      <template v-for="(item, index) in widgets">
        <template v-if="!item.previousChunkIsLoaded" />

        <BannerWidget
          v-else
          v-show="(index + 1) <= visibleBanners"
          :key="item.code"
          :widget="item"
          :page-id="selectedPage ? selectedPage.id : null"
          :hide-link="filters.hideLink"
          @load="() => onWidgetLoad(index)"
        />
      </template>
    </div>

    <div class="banners-show-more-wrapper">
      <b-button
        v-if="widgets.length && widgets.length > visibleBanners"
        key="showBannerButton"
        class="modern-button is-outlined"
        @click="onShowBannerClick"
      >
        SHOW MORE
      </b-button>

      <b-button
        v-else-if="page > 1"
        key="hideBannerButton"
        class="modern-button is-outlined"
        @click="onHideBannerClick"
      >
        HIDE
      </b-button>
    </div>
 </div>
</template>

<script>
import {
  mapActions,
  mapGetters,
  mapMutations,
  mapState,
} from 'vuex';
import * as MUTATIONS from '@/store/mutations';
import NotificationFinancingPage from '@/components/NotificationFinancingPage.vue';
import { getTooltipPositionByWindowWidth } from '@/utils/getTooltipPosition';
import * as ACTIONS from '@/store/actions';
import { search as searchThemes } from '@/api/marketing_materials_theme';
import {
  MARKETING_MATERIALS_ACCESS,
  MARKETING_MATERIALS_MANAGE_ACCESS,
} from '@/const/privileges';
import { banners } from '@/api/widget';
import BannerWidget from '@/views/MarketingMaterials/Banners/BannerWidget.vue';
import ModernRadioTabs from '@/components/Form/ModernRadioTabs.vue';
import { CUSTOMER_TYPE_CONTRACTOR_ID, CUSTOMER_TYPE_MEDICAL_ID } from '@/const/constants';
import PageSelector from '@/views/MarketingMaterials/components/PageSelector.vue';
import { getSizes } from '@/api/banner';

export default {
  name: 'Banners',
  components: {
    PageSelector,
    ModernRadioTabs,
    BannerWidget,
    NotificationFinancingPage,
  },
  data: () => ({
    bannerSizes: [],
    themes: [],
    widgets: [],
    filters: {
      themeId: null,
      bannerSizeKey: null,
      hideLink: true,
    },
    bannerWrapperWidth: null,
    page: 1,
    widgetIndexLoadingMap: {},
    MARKETING_MATERIALS_ACCESS,
    MARKETING_MATERIALS_MANAGE_ACCESS,
    isActivated: false,
  }),
  deactivated() {
    this.isActivated = false;
    this.widgets = [];
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  },
  async activated() {
    this.observeBannersWrapperWidth();
    if (!this.contractorId && !this.partnerId) {
      this.isActivated = true;
      return;
    }

    this.startLoading();

    try {
      if (!this.selectedPage?.id) {
        await this.loadPages(this.contractorId ? this.contractorId : null);
      }

      if (this.selectedPage?.id) {
        const [bannerSizesResponse, themesResponse] = await Promise.all([
          getSizes(this.userId),
          searchThemes({ userTypeId: this.userTypeId }),
        ]);

        this.bannerSizes = bannerSizesResponse.data;
        this.filters.bannerSizeKey = this.bannerSizes[0].key;

        this.themes = themesResponse.data;
        this.filters.themeId = this.themes[0].id;

        await this.loadBanners();
      }

      window.scrollTo(0, 0);
    } catch (error) {
      this.$buefy.snackbar.open({
        message: 'An unexpected error occurred, please contact support@enhancify.com',
        actionText: ' ',
        position: 'is-top',
        indefinite: true,
        type: 'is-danger',
      });
    }

    this.finishLoading();
    this.isActivated = true;
  },
  computed: {
    ...mapState('contractor', {
      contractorId: (state) => state.id,
      contractorTypeId: (state) => state.typeId,
    }),
    ...mapState('partner', { partnerId: (state) => state.id }),
    ...mapState('user', { userId: (state) => state.id }),
    ...mapGetters(['hasAccess', 'pages', 'selectedPage', 'userTypeId']),
    windowWidth() {
      return this.$store.state.windowWidth;
    },
    text() {
      let text = 'Add banners to your website to show your ';

      if (this.contractorId) {
        if (this.contractorTypeId === CUSTOMER_TYPE_CONTRACTOR_ID) {
          text += 'customers';
        } else if (this.contractorTypeId === CUSTOMER_TYPE_MEDICAL_ID) {
          text += 'patients';
        } else {
          text += 'clients';
        }

        text += ' that you offer financing. When visitors of your site click on the Enhancify banner, they will get redirected to your co-branded financing page at Enhancify.com';
      } else if (this.partnerId) {
        text += 'clients they can offer financing to their customers. When your website visitors click on the Enhancify banner, they will be redirected to your co-branded financing page on Enhancify.com';
      }

      return text;
    },
    visibleBanners() {
      let visibleBanners = 0;

      const bannerSize = this.bannerSizes
        .find((item) => item.key === this.filters.bannerSizeKey);

      if (bannerSize) {
        const gap = 35;
        const wrapperLeftRightMargin = 17.5;
        const rowWidth = this.bannerWrapperWidth + gap - (2 * wrapperLeftRightMargin);
        const bannerWidth = +bannerSize.width + gap;
        let elementsPerRow = Math.floor(rowWidth / bannerWidth);
        elementsPerRow = elementsPerRow < 1 ? 1 : elementsPerRow;

        if (['930x180', '728x90'].includes(this.filters.bannerSizeKey)) {
          if (bannerSize.width > this.bannerWrapperWidth) {
            visibleBanners = 3;
          } else {
            visibleBanners = 3 * elementsPerRow;
          }
        } else if (['400x400', '300x600'].includes(this.filters.bannerSizeKey)) {
          if (elementsPerRow <= 1) {
            visibleBanners = 3;
          } else if (elementsPerRow >= 2) {
            visibleBanners = elementsPerRow;
          }
        }
      }
      return visibleBanners * this.page;
    },
  },
  watch: {
    'filters.themeId': 'loadBannersByWatcher',
    'filters.bannerSizeKey': 'loadBannersByWatcher',
    'filters.hideLink': 'loadBannersByWatcher',
    selectedPage: 'loadBannersByWatcher',
  },
  methods: {
    getTooltipPositionByWindowWidth,
    ...mapActions({
      loadPages: ACTIONS.LOAD_PAGES,
      setSelectedPage: ACTIONS.SET_SELECTED_PAGE,
    }),
    ...mapMutations({
      startLoading: MUTATIONS.START_LOADING,
      finishLoading: MUTATIONS.FINISH_LOADING,
    }),
    onShowBannerClick() {
      this.page += 1;
      this.recalculatePreviousChunkIsLoaded();
    },
    onHideBannerClick() {
      this.page = 1;
    },
    recalculatePreviousChunkIsLoaded() {
      for (let i = 0; i < this.widgets.length; i += 1) {
        if (!this.widgets[i].previousChunkIsLoaded) {
          this.widgets[i].previousChunkIsLoaded = this.previousChunkIsLoaded(i);
        }
      }
    },
    previousChunkIsLoaded(index) {
      const number = index + 1;
      const chunkSize = this.visibleBanners / this.page;
      const nextChunkEnd = chunkSize * (this.page + 1);

      if (number <= this.visibleBanners) {
        return true;
      }

      if (number > nextChunkEnd) {
        return false;
      }

      const previousChunkStart = this.visibleBanners - chunkSize;

      for (let i = previousChunkStart; i < this.visibleBanners; i += 1) {
        if (this.widgetIndexLoadingMap[i] !== true) {
          return false;
        }
      }

      return true;
    },
    onWidgetLoad(index) {
      this.widgetIndexLoadingMap[index] = true;

      this.recalculatePreviousChunkIsLoaded();
    },
    async loadBannersByWatcher() {
      if (this.isActivated) {
        this.page = 1;
        await this.loadBanners();
      }
    },
    async loadBanners() {
      this.widgets = [];
      if (!this.selectedPage?.id) {
        return;
      }

      this.startLoading();

      try {
        this.widgetIndexLoadingMap = {};
        this.page = 1;
        const bannerSize = this.bannerSizes
          .find((item) => item.key === this.filters.bannerSizeKey);

        const bannersResponse = await banners({
          pageId: this.selectedPage.id,
          userId: this.userId,
          width: bannerSize.width,
          height: bannerSize.height,
          hideLink: !this.filters.hideLink ? 1 : 0,
          userTypeId: this.userTypeId,
          themeId: this.filters.themeId,
        });

        this.widgets = bannersResponse.data.data.map((widget) => ({
          ...widget,
          previousChunkIsLoaded: false,
        }));
        this.recalculatePreviousChunkIsLoaded();
      } catch (e) {
        this.widgets = [];
        this.$buefy.snackbar.open({
          message: 'An unexpected error occurred, please contact support@enhancify.com',
          actionText: ' ',
          position: 'is-top',
          indefinite: true,
          type: 'is-danger',
        });
      }

      this.finishLoading();
    },
    observeBannersWrapperWidth() {
      const { bannersWrapper } = this.$refs;
      if (bannersWrapper) {
        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
        }

        this.resizeObserver = new ResizeObserver(([entry]) => {
          this.bannerWrapperWidth = entry.contentRect.width;
        });

        this.resizeObserver.observe(bannersWrapper);
      }
    },
  },
};
</script>

<style lang="stylus" scoped>
.banners
  >>> .switch.enh-switch
    span.check
      &:before
        box-shadow none
  >>> .custom-label
    color #969ea2
    font-size 16px
    font-weight 400
  >>> .field-filters
    margin-top 18px
  >>> .banners-title
    max-width 860px
.b-tabs.banner-sizes >>> nav ul
  flex-wrap wrap
  width 100%
  li
    a
      height 48px
      min-width 94px
    @media(max-width 437px)
      &:first-child a
        border-bottom-left-radius 0
      &:nth-child(3) a
        border-top-right-radius 4px
      &:last-child
        &.is-active
          border-color #68ba62
          border-left 1px solid
        a
          border-top-right-radius 0
          border-bottom-left-radius 4px
      &:not(.is-active)
        &:nth-child(1) a
          border-bottom none
        &:nth-child(2) a
          border-bottom none
        &:nth-child(3) a
          border-bottom none
        &:nth-child(4)
          border-left 1px solid #dbdbdb
    @media(max-width 344px)
      &:nth-child(2) a
        border-top-right-radius 4px
      &:nth-child(3) a
        border-top-right-radius 0
        border-bottom-left-radius 4px
      &.is-active
        &:nth-child(3)
          border-left 1px solid #68ba62
      &:not(.is-active)
        &:nth-child(3)
          border-left 1px solid #dbdbdb
          a
            border-bottom 1px solid #dbdbdb
        &:nth-child(4)
          border-left unset!important
.banner-theme-selector
  @media(min-width:500px)
    min-width 300px
  &:not(:last-child)
    @media(max-width:499px)
      margin-right 0!important
  .control.banner-selector
    width 100%
    >>>span.select
      width 100%
      height 48px
      &:after
        border-color #525F66
      select
        width 100%
        height 48px
.section-switch
  display flex
  flex-direction column
  .button
    margin 10px;
.chooseSwitch
  display flex
  margin-top: 5px
  .chooseSwitchText
    display inline-flex
    align-items: center
    margin-right: 20px
    color #969ea2

.banners-wrapper
  margin-top calc(35px - 25px)
  display flex
  flex-wrap wrap
  margin-left -17.5px
  margin-right -17.5px
  margin-bottom -25px
.banners-show-more-wrapper
  display flex
  justify-content center
  margin-top 35px
  button
    width 100%
    max-width 262px
</style>
